import React, { useState, TouchEvent, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import StepContent from './StepContent';

const slideInRight = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideInLeft = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOutRight = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const slideOutLeft = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(-100%);
    opacity: 0;
  }
`;

const VisualizationContainer = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 64rem;
  margin: 0 auto;
  touch-action: pan-y pinch-zoom;

  @media (min-width: 640px) {
    padding: 2rem;
  }
`;

const StepNavigation = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0 0.5rem;

  @media (min-width: 640px) {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 2rem;
    padding: 0;
  }
`;

const StepButton = styled.button<{ active: boolean }>`
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  border: 1px solid ${props => props.active ? 'var(--primary)' : 'transparent'};
  color: ${props => props.active ? 'var(--primary)' : 'var(--gray-500)'};
  background: ${props => props.active ? 'white' : 'transparent'};
  cursor: pointer;
  white-space: nowrap;
  width: 100%;
  text-align: center;

  &:hover {
    color: ${props => props.active ? 'var(--primary)' : 'var(--gray-700)'};
    background: ${props => props.active ? 'white' : 'var(--gray-100)'};
  }

  @media (min-width: 640px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    width: auto;
  }
`;

const ContentWrapper = styled.div`
  background: var(--gray-50);
  border-radius: 0.75rem;
  overflow: hidden;
  margin: 0 -0.5rem;
  user-select: none;
  position: relative;

  @media (min-width: 640px) {
    margin: 0;
  }
`;

const StepContainer = styled.div<{ direction: 'left' | 'right' | null }>`
  position: relative;
  animation: ${props => {
    if (!props.direction) return 'none';
    return props.direction === 'right' ? slideInRight : slideInLeft;
  }} 0.3s ease-out forwards;
`;

const CoagulationVisualization: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const totalSteps = 5;

  const handleTouchStart = (e: TouchEvent) => {
    if (isAnimating) return;
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!touchStart || isAnimating) return;

    const currentTouch = e.touches[0].clientX;
    const diff = touchStart - currentTouch;

    // Require a minimum swipe distance of 50px
    if (Math.abs(diff) < 50) return;

    setIsAnimating(true);
    if (diff > 0) {
      // Swipe left - go to next step
      setSwipeDirection('right');
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    } else {
      // Swipe right - go to previous step
      setSwipeDirection('left');
      setCurrentStep(prev => Math.max(prev - 1, 1));
    }

    setTouchStart(null);
  };

  const handleTouchEnd = () => {
    setTouchStart(null);
  };

  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setSwipeDirection(null);
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [isAnimating]);

  return (
    <VisualizationContainer>
      <StepNavigation>
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
          <StepButton
            key={step}
            active={currentStep === step}
            onClick={() => {
              if (isAnimating) return;
              setSwipeDirection(step > currentStep ? 'right' : 'left');
              setIsAnimating(true);
              setCurrentStep(step);
            }}
          >
            Step {step}
          </StepButton>
        ))}
      </StepNavigation>

      <ContentWrapper
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <StepContainer direction={swipeDirection}>
          <StepContent step={currentStep} />
        </StepContainer>
      </ContentWrapper>
    </VisualizationContainer>
  );
};

export default CoagulationVisualization; 